/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片列表头部操作栏组件（使用新的 Redux Slice，专为 photov2 设计）
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-04-22 11:34:34
 */

import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Button, Checkbox, Modal, Dropdown, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import {
  trackPicsPageClickV2,
  trackMutualExclusiveSelectionToast
} from '../../utils/track'
import { formatNum } from '../../../../utils/utils'
import {
  setExportListDrawerVisible,
  setClientDownloadDrawerVisible
} from '../../../../actions/photo'
import { addPhotosToFolders } from '../../../../actions/folder'
import SearchFeedback from '../SearchFeedback'
import SortOrderToggle from '../../../components/SortOrderToggle'
import DownloadButton from './DownloadButton'
import ExportExcelButton from './ExportExcelButton'
import AddToFolderV2 from '../../../components/AddToFolder/AddToFolderV2'
import BatchRemoveWatermarkButton from './BatchRemoveWatermarkButton'
import BatchEditWatermarkButton from './BatchEditWatermarkButton'
import JigsawButton from './JigsawButton'
import DeleteButton from './DeleteButton'
import BatchAddLabelButton from './BatchAddLabelButton'
import type { SuggestedSearchWord } from '../../../../slices/photoFilterSlice'
import {
  selectSearchFilter,
  setSearchFilter as setPhotoFilterQuery
} from '../../../../slices/photoFilterSlice'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection
} from '../../utils/selectionHelpers'
import { setIsAscSort as setContentIsAscSort } from '../../../../slices/photosByContentSlice'
import CONFIG from '../../../../constants/config'
import type { RootState } from '../../../../app/store'
import desktopLib from '../../../../utils/desktopLib'
import { supportsNewDownloadFeature } from '../../../../utils/versionUtils'
import GuessYouWantSearch from '../GuessYouWantSearch'
import { MessageOutlined, MoreOutlined } from '@ant-design/icons'
import { usePhotoLabelEnabled } from '../../../../app/hooks/usePhotoLabelEnabled'
import { useIsAdmin } from '../../../../app/hooks'
import FeedbackDrawer from '../../../photoTag/components/FeedbackDrawer'
import { trackPhotoLabelFeedbackEnter } from '../../../photoTag/utils/track'

// antd 不再使用 Menu.Item，改用 Dropdown menu={{ items }} 模式
type SuggestedWord = SuggestedSearchWord & Record<string, any>

interface HeaderExtraProps {
  onSearch?: (
    newQueryFilter?: any,
    index?: number,
    searchTrackingId?: string,
    isAscSort?: boolean
  ) => void
  onSortChange?: (isAscSort: boolean) => void
  isAsc?: boolean
  dataSource?: PhotoSelectionSource
  searchType?: '普通猜你想搜' | '向量化猜你想搜'
}

const HeaderExtra: React.FC<HeaderExtraProps> = ({
  onSearch,
  onSortChange,
  isAsc,
  dataSource = 'content',
  searchType = '普通猜你想搜'
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const myRole = useSelector((state: RootState) => state.group.myRole)
  const photoLabelEnabled = usePhotoLabelEnabled()
  const isAdmin = useIsAdmin()
  const [feedbackVisible, setFeedbackVisible] = useState(false)

  const sensorClickItem = useCallback((clickItem: string) => {
    trackPicsPageClickV2({ clickItem })
  }, [])

  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos,
    activePhotosNum,
    activeCheckAll,
    activeCheckedPhotos,
    activeUnCheckedPhotos,
    mainPhotos,
    mainPhotosNum,
    mainCheckAll,
    mainCheckedPhotos,
    mainUnCheckedPhotos,
    suggestedPhotos,
    suggestedPhotosNum,
    suggestedCheckAll,
    suggestedCheckedPhotos,
    suggestedUnCheckedPhotos
  } = useActiveSelection(dataSource)

  const photos = activePhotos
  const photosNum = activePhotosNum
  const checkAll = activeCheckAll
  const checkedPhotos = activeCheckedPhotos
  const unCheckedPhotos = activeUnCheckedPhotos

  const queryFilter = useSelector(selectSearchFilter)
  const activeTab = useSelector(
    (state: RootState) => state.allPhoto.photoListActiveTab
  )
  const searchTrackingId = (queryFilter as any)?.searchTrackingId

  // 判断是否显示"更多"菜单：只在正常页面（content）显示，精准搜索（precise）不显示
  const showMoreMenu = dataSource === 'content'
  const suggestedSearchWords: SuggestedWord[] = Array.isArray(
    queryFilter?.suggestedSearchWords
  )
    ? (queryFilter?.suggestedSearchWords as SuggestedWord[])
    : []
  const shouldShowGuessYouWant = showMoreMenu && suggestedSearchWords.length > 0
  const primaryKeyword = queryFilter?.searchKeywords?.[0] || ''

  // 计算已选择的照片数量
  const checkedPhotoNum = useMemo(() => {
    if (hasSuggestedSelected) {
      if (suggestedCheckAll) {
        return suggestedPhotosNum - suggestedUnCheckedPhotos.size
      }
      return suggestedCheckedPhotos.size
    }
    if (mainCheckAll) {
      return mainPhotosNum - mainUnCheckedPhotos.size
    }
    return mainCheckedPhotos.size
  }, [
    hasSuggestedSelected,
    suggestedCheckAll,
    suggestedPhotosNum,
    suggestedCheckedPhotos.size,
    suggestedUnCheckedPhotos.size,
    mainCheckAll,
    mainPhotosNum,
    mainCheckedPhotos.size,
    mainUnCheckedPhotos.size
  ])

  // 判断是否显示取消选择按钮
  const isShowCancelBtn = useMemo(() => {
    return (
      mainCheckedPhotos.size > 0 ||
      mainCheckAll ||
      suggestedCheckedPhotos.size > 0 ||
      suggestedCheckAll
    )
  }, [
    mainCheckedPhotos.size,
    mainCheckAll,
    suggestedCheckedPhotos.size,
    suggestedCheckAll
  ])

  // 处理全选（仅针对主列表）
  const handleCheckAll = useCallback(
    (e: any) => {
      if (e.target.checked && hasSuggestedSelected) {
        trackMutualExclusiveSelectionToast()
        message.warning('原始筛选结果与推荐结果不可同时选中')
        return
      }

      if (e.target.checked) {
        dispatch(selectionConfig.setCheckAllAction())
      } else {
        dispatch(selectionConfig.clearCheckAction())
      }
      trackPicsPageClickV2({
        clickItem: e.target.checked ? 'chooseAll' : 'cancelChooseAll'
      })
    },
    [dispatch, selectionConfig, hasSuggestedSelected]
  )

  // 处理取消选择（自动清除所有选中状态）
  const handleCancelCheckAll = useCallback(() => {
    if (hasSuggestedSelected) {
      dispatch({ type: 'photosByContent/clearSuggestedCheck' })
    } else {
      dispatch(selectionConfig.clearCheckAction())
    }
    trackPicsPageClickV2({ clickItem: 'cancelChoose' })
  }, [dispatch, selectionConfig, hasSuggestedSelected])

  // 排序状态管理
  const [localIsAscSort, setLocalIsAscSort] = useState(isAsc ?? false)

  useEffect(() => {
    if (isAsc !== undefined) {
      setLocalIsAscSort(isAsc)
    }
  }, [isAsc])

  // 处理排序切换
  const handleSortToggle = (nextIsAsc: boolean) => {
    trackPicsPageClickV2({
      clickItem: nextIsAsc ? 'clickPositiveOrder' : 'clickReverseOrder'
    })

    setLocalIsAscSort(nextIsAsc)

    if (onSortChange) {
      onSortChange(nextIsAsc)
    }
  }

  const handleGuessYouWantToggle = useCallback(
    (index: number) => {
      if (!onSearch || !queryFilter || !suggestedSearchWords[index]) {
        return
      }

      const nextSuggestedSearchWords = suggestedSearchWords.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            isChecked: !item.isChecked
          }
        }
        return item
      })

      const nextQueryFilter = {
        ...queryFilter,
        suggestedSearchWords: nextSuggestedSearchWords
      }

      const currentWord = nextSuggestedSearchWords[index]
      const clickItem = currentWord?.isChecked
        ? 'wantSearch'
        : 'cancelWantSearch'
      const number2 = nextSuggestedSearchWords.filter(
        (item) => item.isChecked
      ).length
      const list1 = nextSuggestedSearchWords.map((item) => item.word)
      const list2 = nextSuggestedSearchWords
        .filter((item) => item.isChecked)
        .map((item) => item.word)

      trackPicsPageClickV2({
        clickItem,
        tabName: activeTab,
        rankNum: index + 1,
        number1: nextSuggestedSearchWords.length,
        number2,
        list1,
        list2,
        string1: currentWord?.source,
        string3: searchTrackingId,
        string4: currentWord?.isLocked ? '搜索词' : searchType
      })

      setLocalIsAscSort(false)
      if (showMoreMenu) {
        dispatch(setContentIsAscSort(false))
      }
      dispatch(setPhotoFilterQuery(nextQueryFilter))
      onSearch(nextQueryFilter, index, searchTrackingId, false)
    },
    [
      activeTab,
      dispatch,
      groupId,
      myRole,
      onSearch,
      queryFilter,
      searchTrackingId,
      showMoreMenu,
      suggestedSearchWords
    ]
  )

  // 处理搜索关键词标签显示
  const searchKeywordLabels = useMemo(() => {
    if (!queryFilter?.searchKeywords) return []
    return queryFilter.searchKeywords.filter(
      (keyword: string) => typeof keyword === 'string' && keyword.trim()
    )
  }, [queryFilter?.searchKeywords])

  const shouldShowKeywordLabel = Boolean(
    queryFilter?.isNlpFilter && searchKeywordLabels.length > 0
  )

  // "更多"菜单 - antd 使用 menu={{ items }} 模式
  const menuItems = useMemo(
    () => [
      {
        key: 'jigsaw',
        label: <JigsawButton dataSource={dataSource} />
      },
      {
        key: 'downloadRecord',
        label: (
          <Button
            type="link"
            onClick={() => {
              trackPicsPageClickV2({ clickItem: 'downloadRecord' })

              if (desktopLib.isDesktop && supportsNewDownloadFeature()) {
                dispatch(setClientDownloadDrawerVisible(true))
                return
              }

              dispatch(setExportListDrawerVisible(true))
            }}
          >
            下载记录
          </Button>
        )
      },
      ...(photoLabelEnabled
        ? [
            {
              key: 'batchAddLabel',
              label: (
                <BatchAddLabelButton
                  dataSource={dataSource}
                  onRefresh={(snapshotFilter) => {
                    if (onSearch) {
                      // 用批量添加发起时的 queryFilter 快照搜索，
                      // 防止用户在批量添加期间修改过滤条件导致搜索参数错误
                      onSearch(snapshotFilter)
                    }
                  }}
                />
              )
            }
          ]
        : []),
      {
        key: 'delete',
        label: <DeleteButton dataSource={dataSource} />
      },
      {
        key: 'deleteRecord',
        label: (
          <Button
            type="link"
            onClick={() => {
              sensorClickItem('deleteRecord')
              navigate('/photo/delete-records')
            }}
          >
            删除记录
          </Button>
        )
      }
    ],
    [
      dispatch,
      navigate,
      sensorClickItem,
      dataSource,
      onSearch,
      photoLabelEnabled
    ]
  )

  // 处理添加到文件夹
  const handleAddToFolder = useCallback(
    ({
      folderIDs,
      submitCallback
    }: {
      folderIDs: string[]
      submitCallback: () => void
    }) => {
      // 生成照片ID数组
      let photoIDs: string[] = []

      if (hasSuggestedSelected) {
        if (suggestedCheckAll) {
          photoIDs = suggestedPhotos
            .filter((photo) => !suggestedUnCheckedPhotos.has(photo.seq))
            .map((photo) => photo.seq.toString())
        } else {
          photoIDs = Array.from(suggestedCheckedPhotos).map((seq: any) =>
            seq.toString()
          )
        }
      } else {
        if (mainCheckAll) {
          photoIDs = mainPhotos
            .filter((photo) => !mainUnCheckedPhotos.has(photo.seq))
            .map((photo) => photo.seq.toString())
        } else {
          photoIDs = Array.from(mainCheckedPhotos).map((seq: any) =>
            seq.toString()
          )
        }
      }

      const saReport = (clickItem: string) => {
        sensors.track('web_get_action_pop_workgroup_upload_photo_limit', {
          clickItem,
          role: sensorsRole(myRole),
          groupID: groupId
        })
      }

      const submit = () => {
        const action = addPhotosToFolders({
          groupID: groupId,
          folderIDs,
          photoIDs: photoIDs.slice(0, CONFIG.folderConfig.batchAddPhotoLimit),
          source: 5
        })
        const result = dispatch(action as any)
        if (result && typeof result.then === 'function') {
          result.then(() => {
            submitCallback()
          })
        } else {
          submitCallback()
        }
      }

      if (photoIDs.length > CONFIG.folderConfig.batchAddPhotoLimit) {
        saReport('show')

        Modal.confirm({
          title: `一次最多可添加${CONFIG.folderConfig.batchAddPhotoLimit}张`,
          okText: `添加所选前${CONFIG.folderConfig.batchAddPhotoLimit}张`,
          icon: 'info-circle',
          onOk: () => {
            saReport(`addFirst${CONFIG.folderConfig.batchAddPhotoLimit}`)
            submit()
          },
          onCancel: () => {
            saReport('cancel')
          }
        })
      } else {
        submit()
      }
    },
    [
      hasSuggestedSelected,
      suggestedCheckAll,
      suggestedPhotos,
      suggestedUnCheckedPhotos,
      suggestedCheckedPhotos,
      mainCheckAll,
      mainPhotos,
      mainUnCheckedPhotos,
      mainCheckedPhotos,
      dispatch,
      groupId,
      myRole
    ]
  )

  return (
    <>
      <div className="header-extra">
        <div className="left">
          <p className="count">
            {shouldShowKeywordLabel && (
              <span className="count-keywords">
                {searchKeywordLabels.map((keyword, index) => (
                  <span key={index} className="keyword-item">
                    {'"'}
                    {keyword}
                    {'"'}
                  </span>
                ))}
              </span>
            )}
            共筛选出<strong>{formatNum(mainPhotosNum)}</strong>张照片
          </p>
          <Checkbox
            style={{ marginLeft: 10 }}
            disabled={mainPhotosNum === 0}
            indeterminate={
              (mainCheckAll && mainUnCheckedPhotos.size > 0) ||
              (!mainCheckAll && mainCheckedPhotos.size > 0)
            }
            checked={mainCheckAll}
            onChange={handleCheckAll}
          >
            全选
          </Checkbox>
          <p style={{ marginLeft: 10 }}>
            已选择
            <strong>{formatNum(checkedPhotoNum)}</strong>
            张照片
            <SortOrderToggle
              isAsc={localIsAscSort}
              onToggle={handleSortToggle}
            />
          </p>
          {isShowCancelBtn && (
            <>
              ，
              <p
                className="cancel"
                style={{ color: '#0093ff', cursor: 'pointer' }}
                onClick={handleCancelCheckAll}
              >
                取消选择
              </p>
            </>
          )}
          <SearchFeedback
            onFeedbackClick={() => {
              trackPicsPageClickV2({
                clickItem: 'resultFeedback',
                tabName: activeTab
              })
            }}
          />
          {/* 试用标签入口已下线：白名单内团队走正式 photo_label 入口，
              白名单外团队不再展示任何 photo_label 相关 UI */}
          {photoLabelEnabled && isAdmin && (
            <span
              className="photo-label-feedback-entry"
              style={{ marginLeft: 10, color: '#0093ff', cursor: 'pointer' }}
              onClick={() => {
                trackPhotoLabelFeedbackEnter({
                  elementFrom: 'list',
                  enterFrom: 'web_picspage'
                })
                setFeedbackVisible(true)
              }}
            >
              <MessageOutlined /> 标签反馈
            </span>
          )}
        </div>
        <div className="right">
          <DownloadButton dataSource={dataSource} />
          {checkedPhotoNum > 0 && (
            <>
              <ExportExcelButton dataSource={dataSource} />
              <AddToFolderV2
                onSubmit={handleAddToFolder}
                checkedNum={checkedPhotoNum}
                isRequireCheckPhoto={true}
                place="allPhoto"
                source={4}
                isPermanent={true}
                dataSource={dataSource}
                sensorReport={() => {
                  trackPicsPageClickV2({ clickItem: 'addFolder' })
                }}
              />
              <BatchEditWatermarkButton
                dataSource={dataSource}
                sensorReport={() => {
                  trackPicsPageClickV2({ clickItem: 'batchEditWatermark' })
                }}
              />
              <BatchRemoveWatermarkButton
                dataSource={dataSource}
                sensorReport={() => {
                  trackPicsPageClickV2({ clickItem: 'batchRemoveWatermark' })
                }}
              />
            </>
          )}
          {showMoreMenu && (
            <Dropdown menu={{ items: menuItems }}>
              <Button
                type="link"
                style={{ padding: 0, color: '#999999', fontSize: 20 }}
              >
                <MoreOutlined />
              </Button>
            </Dropdown>
          )}
        </div>
      </div>
      {shouldShowGuessYouWant && (
        <GuessYouWantSearch
          suggestedSearchWords={suggestedSearchWords}
          highlightKeyword={primaryKeyword}
          onToggle={handleGuessYouWantToggle}
          searchType={searchType}
        />
      )}

      <FeedbackDrawer
        visible={feedbackVisible}
        onClose={() => setFeedbackVisible(false)}
        source="list"
      />
    </>
  )
}

export default HeaderExtra
